
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckIcon, Pencil, Trash2, Eye } from "lucide-react";
import { Tables } from "@/integrations/supabase/types";

type Testimonial = Tables<"testimonials">;

interface TestimonialListProps {
  testimonials: Testimonial[];
  isLoading: boolean;
  onApprove: (id: string) => void;
  onDelete: (id: string) => void;
  onEdit: (testimonial: Testimonial) => void;
  onPreview?: (testimonial: Testimonial) => void;
}

const TestimonialList = ({
  testimonials,
  isLoading,
  onApprove,
  onDelete,
  onEdit,
  onPreview
}: TestimonialListProps) => {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-4 w-1/3 mt-2" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-20 w-full" />
            </CardContent>
            <CardFooter>
              <Skeleton className="h-10 w-24 mr-2" />
              <Skeleton className="h-10 w-24" />
            </CardFooter>
          </Card>
        ))}
      </div>
    );
  }

  if (testimonials.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <p className="text-muted-foreground">No testimonials found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {testimonials.map((testimonial) => (
        <Card key={testimonial.id}>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {testimonial.author_name || "Anonymous"}
                  {testimonial.rating && (
                    <span className="text-sm font-normal text-yellow-500">
                      {'★'.repeat(testimonial.rating)}
                      {'☆'.repeat(5 - (testimonial.rating || 0))}
                    </span>
                  )}
                </CardTitle>
                <CardDescription className="flex gap-2 mt-1">
                  {testimonial.company_name && (
                    <span>{testimonial.company_name}</span>
                  )}
                  {testimonial.role_title && (
                    <span>{testimonial.role_title}</span>
                  )}
                  {testimonial.industry && (
                    <span>{testimonial.industry}</span>
                  )}
                </CardDescription>
              </div>
              <Badge variant={testimonial.approved ? "default" : "outline"}>
                {testimonial.approved ? "Approved" : "Pending"}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-line">{testimonial.content}</p>
          </CardContent>
          <CardFooter className="flex justify-end gap-2">
            {!testimonial.approved && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => onApprove(testimonial.id)}
                className="flex items-center gap-1"
              >
                <CheckIcon className="h-4 w-4" />
                Approve
              </Button>
            )}
            {onPreview && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => onPreview(testimonial)}
                className="flex items-center gap-1"
              >
                <Eye className="h-4 w-4" />
                Preview
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => onEdit(testimonial)}
              className="flex items-center gap-1"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
            <Button 
              variant="destructive" 
              size="sm" 
              onClick={() => onDelete(testimonial.id)}
              className="flex items-center gap-1"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
};

export default TestimonialList;
